import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  ChefHat,
  Eye,
  Heart,
  Pencil,
  Plus,
  Search,
  Settings2,
  Sparkles,
  Trash2,
} from "lucide-react";
import LoadingOverlay from "../../components/Loading/LoadingOverlay";
import Modal from "../../components/Dialog/Modal";
import CustomAlert from "../../components/Alerts/CustomAlert";
import { addAlert, removeAlert } from "../../Utils/helper";
import {
  deleteRecipe,
  getApiError,
  getRecipeSummary,
  listRecipeCategories,
  listRecipes,
  toggleRecipeActive,
} from "../../Utils/recipeAdminApi";
import RecipeSectionHelp from "./RecipeSectionHelp";
import "./RecipesAdmin.css";

const emptySummary = {
  total: 0,
  published: 0,
  drafts: 0,
  ai_generated: 0,
  total_views: 0,
};

const RecipesList = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dietFilter, setDietFilter] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const notify = (title, message, type) =>
    addAlert(title, message, type, true, setAlerts);

  const params = useMemo(() => {
    const value = { page_size: 200 };
    if (search.trim()) value.search = search.trim();
    if (statusFilter) value.is_active = statusFilter;
    if (dietFilter) value.diet_type = dietFilter;
    if (difficultyFilter) value.difficulty_level = difficultyFilter;
    if (categoryFilter) value.category = categoryFilter;
    return value;
  }, [search, statusFilter, dietFilter, difficultyFilter, categoryFilter]);

  const load = async () => {
    setIsLoading(true);
    try {
      const [recipeResponse, summaryResponse] = await Promise.all([
        listRecipes(params),
        getRecipeSummary(),
      ]);
      setRecipes(recipeResponse.items);
      setSummary({ ...emptySummary, ...(summaryResponse || {}) });
    } catch (error) {
      notify("Could not load recipes", getApiError(error), "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    listRecipeCategories({ page_size: 200 })
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const timeout = setTimeout(load, 220);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const toggleActive = async (recipe) => {
    try {
      await toggleRecipeActive(recipe.id);
      notify(
        recipe.is_active ? "Moved to draft" : "Recipe published",
        recipe.title,
        "success"
      );
      await load();
    } catch (error) {
      notify("Status not changed", getApiError(error), "error");
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteRecipe(pendingDelete.id);
      notify("Recipe deleted", pendingDelete.title, "success");
      setPendingDelete(null);
      await load();
    } catch (error) {
      notify("Could not delete recipe", getApiError(error), "error");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setDietFilter("");
    setDifficultyFilter("");
    setCategoryFilter("");
  };

  const hasFilters =
    search || statusFilter || dietFilter || difficultyFilter || categoryFilter;

  return (
    <div className="recipe-admin-page">
      {isLoading && <LoadingOverlay />}

      <header className="recipe-page-hero">
        <div>
          <span className="recipe-eyebrow">
            <ChefHat size={15} /> Content operations
          </span>
          <h1>Recipe studio</h1>
          <p>
            Create, publish, and maintain the full recipe catalogue from one
            admin-only workspace.
          </p>
        </div>
        <div className="recipe-hero-actions">
          <button
            className="recipe-button recipe-button-secondary"
            onClick={() => navigate("/recipes/setup")}
          >
            <Settings2 size={17} /> Manage setup
          </button>
          <button
            className="recipe-button recipe-button-primary"
            onClick={() => navigate("/recipes/new")}
          >
            <Plus size={17} /> Add recipe
          </button>
        </div>
      </header>

      <RecipeSectionHelp title="How to use the recipe studio">
        <p>
          This catalogue is the admin source of truth for recipes shown in the
          MyBurgo app. Use filters to find a recipe, then edit its complete
          content or change its publishing status from the card.
        </p>
        <ul>
          <li>
            <strong>Published</strong> recipes are eligible to appear in the
            app; drafts stay hidden.
          </li>
          <li>
            <strong>Add recipe</strong> opens the guided editor for content,
            classification, ingredients, directions, and nutrition.
          </li>
          <li>
            <strong>Manage setup</strong> covers every supporting Django admin
            model: content rows, tags, collections, home sections, engagement,
            cook sessions, meal plans, and runtime configuration.
          </li>
          <li>
            Deleting a recipe also removes its attached directions, ingredients,
            nutrition, and engagement history.
          </li>
        </ul>
      </RecipeSectionHelp>

      <section className="recipe-stat-grid" aria-label="Recipe overview">
        <article className="recipe-stat-card stat-total">
          <span className="recipe-stat-icon">
            <BookOpen size={20} />
          </span>
          <div>
            <strong>{summary.total}</strong>
            <span>Total recipes</span>
          </div>
        </article>
        <article className="recipe-stat-card stat-live">
          <span className="recipe-stat-icon">
            <Sparkles size={20} />
          </span>
          <div>
            <strong>{summary.published}</strong>
            <span>Published</span>
          </div>
        </article>
        <article className="recipe-stat-card stat-draft">
          <span className="recipe-stat-icon">
            <Pencil size={20} />
          </span>
          <div>
            <strong>{summary.drafts}</strong>
            <span>In draft</span>
          </div>
        </article>
        <article className="recipe-stat-card stat-views">
          <span className="recipe-stat-icon">
            <Eye size={20} />
          </span>
          <div>
            <strong>{Number(summary.total_views).toLocaleString()}</strong>
            <span>Catalogue views</span>
          </div>
        </article>
      </section>

      <section className="recipe-panel recipe-catalogue-panel">
        <div className="recipe-panel-heading">
          <div>
            <h2>All recipes</h2>
            <p>{recipes.length} results in this view</p>
          </div>
          <span className="recipe-admin-lock">Admins only</span>
        </div>

        <div className="recipe-filter-bar">
          <label className="recipe-search-field">
            <Search size={17} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search title, chef, or slug"
            />
          </label>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="">Any status</option>
            <option value="true">Published</option>
            <option value="false">Draft</option>
          </select>
          <select
            value={difficultyFilter}
            onChange={(event) => setDifficultyFilter(event.target.value)}
          >
            <option value="">Any difficulty</option>
            <option value="1">Easy</option>
            <option value="2">Medium</option>
            <option value="3">Hard</option>
          </select>
          <select
            value={dietFilter}
            onChange={(event) => setDietFilter(event.target.value)}
          >
            <option value="">Any diet</option>
            <option value="veg">Vegetarian</option>
            <option value="nonveg">Non-vegetarian</option>
            <option value="contains_egg">Contains egg</option>
          </select>
          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
          >
            <option value="">All categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.title}
              </option>
            ))}
          </select>
          {hasFilters && (
            <button className="recipe-clear-button" onClick={clearFilters}>
              Clear
            </button>
          )}
        </div>

        {recipes.length === 0 && !isLoading ? (
          <div className="recipe-empty-state">
            <span>
              <ChefHat size={28} />
            </span>
            <h3>No recipes found</h3>
            <p>Change the filters or add the first recipe to this catalogue.</p>
            <button
              className="recipe-button recipe-button-primary"
              onClick={() => navigate("/recipes/new")}
            >
              <Plus size={17} /> Add recipe
            </button>
          </div>
        ) : (
          <div className="recipe-card-grid">
            {recipes.map((recipe) => (
              <article className="recipe-catalogue-card" key={recipe.id}>
                <div className="recipe-card-media">
                  {recipe.card_image || recipe.banner ? (
                    <img src={recipe.card_image || recipe.banner} alt="" />
                  ) : (
                    <div className="recipe-card-placeholder">
                      <ChefHat size={30} />
                    </div>
                  )}
                  <span
                    className={`recipe-status-pill ${
                      recipe.is_active ? "is-live" : "is-draft"
                    }`}
                  >
                    {recipe.is_active ? "Published" : "Draft"}
                  </span>
                  {recipe.is_ai_generated && (
                    <span className="recipe-ai-pill">
                      <Sparkles size={12} /> AI
                    </span>
                  )}
                </div>
                <div className="recipe-card-content">
                  <div className="recipe-card-kicker">
                    {recipe.difficulty || "Unrated"} · {recipe.diet_type || "—"}
                  </div>
                  <h3>{recipe.title}</h3>
                  <p>{recipe.chef_name || "No chef assigned"}</p>
                  <div className="recipe-category-row">
                    {(recipe.category_names || []).slice(0, 2).map((name) => (
                      <span key={name}>{name}</span>
                    ))}
                  </div>
                  <div className="recipe-card-metrics">
                    <span>
                      <Eye size={14} /> {recipe.view_count || 0}
                    </span>
                    <span>
                      <Heart size={14} /> {recipe.like_count || 0}
                    </span>
                    <span>{recipe.ingredient_count || 0} ingredients</span>
                  </div>
                </div>
                <div className="recipe-card-actions">
                  <button
                    className="recipe-card-edit"
                    onClick={() => navigate(`/recipes/${recipe.id}/edit`)}
                  >
                    <Pencil size={15} /> Edit recipe
                  </button>
                  <button
                    title={recipe.is_active ? "Move to draft" : "Publish"}
                    aria-label={recipe.is_active ? "Move to draft" : "Publish"}
                    onClick={() => toggleActive(recipe)}
                  >
                    <Sparkles size={15} />
                  </button>
                  <button
                    className="is-danger"
                    title="Delete"
                    aria-label="Delete recipe"
                    onClick={() => setPendingDelete(recipe)}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {pendingDelete && (
        <Modal title="Delete recipe?" onClose={() => setPendingDelete(null)}>
          <p className="recipe-modal-copy">
            <strong>{pendingDelete.title}</strong> and its ingredients,
            directions, nutrition, likes, favourites, and view history will be
            permanently removed.
          </p>
          <div className="recipe-modal-actions">
            <button
              className="recipe-button recipe-button-secondary"
              onClick={() => setPendingDelete(null)}
            >
              Cancel
            </button>
            <button
              className="recipe-button recipe-button-danger"
              onClick={confirmDelete}
            >
              Delete recipe
            </button>
          </div>
        </Modal>
      )}

      {alerts.map((alert) => (
        <CustomAlert
          key={alert.id}
          {...alert}
          onClose={() => removeAlert(alert.id, setAlerts)}
        />
      ))}
    </div>
  );
};

export default RecipesList;
